import React from "react";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "./ui/button";
import {
  Video,
  Palette,
  Megaphone,
  Monitor,
  Award,
  Smartphone,
  Camera,
  Edit,
  Tv,
  Globe,
  Target,
  Code,
} from "lucide-react";

const ServicesSection = () => {
  const services = [
    {
      icon: Video,
      title: "Videography",
      description:
        "Produksi video profesional untuk company profile, dokumentasi event, dan konten media sosial dengan kualitas sinematik.",
      features: [
        "Company Profile Video",
        "Event Documentation",
        "Social Media Content",
        "Commercial Video",
      ],
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: Palette,
      title: "Animation",
      description:
        "Kreasi animasi 2D, 3D, dan stop motion yang menarik untuk presentasi, explainer video, dan konten marketing.",
      features: [
        "2D Animation",
        "3D Animation",
        "Stop Motion",
        "Motion Graphics",
      ],
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: Megaphone,
      title: "Advertising",
      description:
        "Strategi dan produksi iklan untuk TV, videotron, dan platform media sosial dengan pendekatan kreatif dan efektif.",
      features: [
        "TV Commercial",
        "Digital Ads",
        "Videotron Content",
        "Social Media Ads",
      ],
      color: "from-orange-500 to-red-500",
    },
    {
      icon: Monitor,
      title: "Multimedia",
      description:
        "Pengembangan website, aplikasi, game, database, dan sistem informasi dengan teknologi terkini.",
      features: [
        "Website Development",
        "Mobile Apps",
        "Game Development",
        "Database Systems",
      ],
      color: "from-green-500 to-teal-500",
    },
    {
      icon: Award,
      title: "Branding",
      description:
        "Strategi branding komprehensif dari penciptaan brand identity hingga promosi untuk membangun citra yang kuat.",
      features: [
        "Brand Strategy",
        "Logo Design",
        "Brand Guidelines",
        "Marketing Materials",
      ],
      color: "from-pink-500 to-rose-500",
    },
    {
      icon: Smartphone,
      title: "Website & Android Apps",
      description:
        "Pengembangan aplikasi custom, sistem bisnis, e-commerce, dan mobile apps yang user-friendly dan responsive.",
      features: [
        "Custom Applications",
        "E-Commerce Platform",
        "Business Systems",
        "Mobile Development",
      ],
      color: "from-indigo-500 to-blue-600",
    },
    {
      icon: Target,
      title: "Training & Development",
      description:
        "Program pelatihan dan pengembangan keterampilan di bidang multimedia, teknologi, dan bisnis untuk meningkatkan kompetensi tim Anda.",
      features: [
        "Pelatihan Multimedia",
        "Workshop Digital Marketing",
        "Kursus Web & Mobile Development",
        "Pengembangan SDM",
      ],
      color: "from-yellow-500 to-orange-500",
    },
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-4">
              Layanan Kami
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Solusi{" "}
              <span className="text-gradient-blue-orange">Multimedia</span>{" "}
              Terlengkap
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Kami menyediakan layanan multimedia komprehensif untuk memenuhi
              semua kebutuhan kreatif dan teknologi bisnis Anda.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="group card-hover border-0 bg-white shadow-lg overflow-hidden h-full"
              >
                <CardHeader className="pb-4 p-4 sm:p-6">
                  <div
                    className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <service.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {service.title}
                  </h3>
                </CardHeader>
                <CardContent className="pt-0 p-4 sm:p-6 flex flex-col justify-between flex-grow">
                  <div>
                    <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                      {service.description}
                    </p>

                    <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                      {service.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-xs sm:text-sm text-gray-600"
                        >
                          <div className="w-3 h-3 sm:w-4 sm:h-4 bg-blue-100 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></div>
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* <Button
                    variant="outline"
                    size="sm"
                    className="w-full group-hover:bg-blue-50 group-hover:border-blue-300 transition-all duration-300 text-sm sm:text-base py-2 sm:py-3"
                  >
                    Pelajari Lebih Lanjut
                  </Button> */}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 rounded-3xl p-8 md:p-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Butuh Solusi Khusus?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Tim ahli kami siap membantu merancang solusi multimedia yang
                sesuai dengan kebutuhan spesifik bisnis Anda.
              </p>
              <a
                href="https://wa.me/6285133079588?text=Halo%20RanselPro!%20Saya%20ingin%20konsultasi%20tentang%20layanan%20multimedia."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-xl btn-hover-scale"
                >
                  Konsultasi Gratis
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
